import PropTypes from "prop-types";
import { forwardRef } from "react";

// material-ui
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// project import
import Highlighter from "./third-party/Highlighter";

// header style
const headerSX = {
  p: 2.5,
  "& .MuiCardHeader-action": { m: "0px auto", alignSelf: "center" },
};

// ==============================|| CUSTOM - MAIN CARD ||============================== //

const MainCard = forwardRef(
  (
    {
      minHeight = null,
      allHeight = false,
      height = null,
      width = null,
      allWidth = false,
      hasPadding = true,
      backgroundColor = "white",
      border = false,
      hasBorderRadius = true,
      boxShadow,
      children,
      content = true,
      contentSX = {},
      darkTitle,
      elevation,
      secondary,
      shadow,
      sx = {},
      title,
      codeHighlight,
      handleClick,
      isClickable = false,
      ...others
    },
    ref
  ) => {
    const theme = useTheme();
    boxShadow = theme.palette.mode === "dark" ? boxShadow || true : boxShadow;

    const realContentSX = {
      ...contentSX,
      my: minHeight ? "auto !important" : undefined,
      height: allHeight ? "100% !important" : "100% !important",
      width: allWidth ? "100% !important" : "100% !important",
      maxHeight: allHeight
        ? "100% !important"
        : height
        ? `${height} !important`
        : undefined,
      maxWidth: allWidth
        ? "100% !important"
        : width
        ? `${width} !important`
        : undefined,
      py: hasPadding ? ".5rem" : "0 !important",
      px: hasPadding ? "1.5rem" : "0 !important",
    };

    return (
      <Card
        elevation={elevation || 0}
        ref={ref}
        {...others}
        sx={{
          display: minHeight ? "flex" : undefined,
          flexDirection: "column",
          minHeight: minHeight ?? undefined,
          height: allHeight ? "100% !important" : "100% !important",
          width: allWidth ? "100% !important" : "100% !important",
          maxHeight: allHeight
            ? "100% !important"
            : height
            ? `${height} !important`
            : undefined,
          maxWidth: allWidth
            ? "100% !important"
            : width
            ? `${width} !important`
            : undefined,
          border: border ? "1px solid" : "none",
          borderRadius: hasBorderRadius ? 2 : "0 !important",
          backgroundColor,
          borderColor:
            theme.palette.mode === "dark"
              ? theme.palette.divider
              : theme.palette.grey.A800,
          boxShadow:
            boxShadow && (!border || theme.palette.mode === "dark")
              ? shadow || "inherit"
              : "inherit",
          cursor: isClickable ? "pointer" : "",
          ":hover": {
            boxShadow: boxShadow ? shadow || "inherit" : "inherit",
          },
          "& pre": {
            m: 0,
            p: hasPadding ? "16px !important" : "0 !important",
            fontFamily: theme.typography.fontFamily,
            fontSize: "0.75rem",
          },
          ...sx,
        }}
        onClick={handleClick}
      >
        {/* card header and action */}
        {!darkTitle && title && (
          <CardHeader
            sx={headerSX}
            titleTypographyProps={{ variant: "subtitle1" }}
            title={title}
            action={secondary}
          />
        )}
        {darkTitle && title && (
          <CardHeader
            sx={headerSX}
            title={<Typography variant="h3">{title}</Typography>}
            action={secondary}
          />
        )}

        {/* card content */}
        {content && <CardContent sx={realContentSX}>{children}</CardContent>}
        {!content && children}

        {/* card footer - clipboard & highlighter  */}
        {codeHighlight && (
          <>
            <Divider sx={{ borderStyle: "dashed" }} />
            <Highlighter codeHighlight={codeHighlight} main>
              {children}
            </Highlighter>
          </>
        )}
      </Card>
    );
  }
);

MainCard.propTypes = {
  height: PropTypes.any,
  width: PropTypes.any,
  minHeight: PropTypes.any,
  allHeight: PropTypes.bool,
  allWidth: PropTypes.bool,
  backgroundColor: PropTypes.string,
  border: PropTypes.bool,
  hasPaddgin: PropTypes.bool,
  hasBorderRadius: PropTypes.bool,
  boxShadow: PropTypes.bool,
  contentSX: PropTypes.object,
  darkTitle: PropTypes.bool,
  divider: PropTypes.bool,
  elevation: PropTypes.number,
  secondary: PropTypes.node,
  shadow: PropTypes.string,
  sx: PropTypes.object,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.any]),
  codeHighlight: PropTypes.bool,
  content: PropTypes.bool,
  children: PropTypes.node | PropTypes.any,
};

export default MainCard;
